import { type NextRouter } from 'next/router';
import { type CONVEYANCE_TYPES, type Location, type CheckoutBasket } from '@koala/sdk';
import { addMinutes } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import { formatDate, isDaySame, formatMinutesToReadableTime } from '@/utils/dates';
import {
  getStoreTimeInfo,
  supportsAsapOrders,
  getLeadTimes,
  getFirstOpenOnDay,
} from '@/utils/locations';
import { DATE_FORMAT } from '@/constants/dates';
import { ASAP_VALUE, TIME_MODES } from '@/constants/global';
import { ROUTES } from '@/constants/routes';

export interface availableDaysType {
  label: string;
  value: string;
  disabled: boolean;
}

export const getReadableRange = (minutes: number) => {
  let range = `${formatMinutesToReadableTime(minutes)} - ${formatMinutesToReadableTime(
    minutes + 10,
  )}`;

  range = minutes + 10 < 60 ? range.replace('min', '') : range;
  return range;
};

export const getPrepMinutes = (
  checkoutBasket: CheckoutBasket,
  router: NextRouter,
  location?: Location,
  handoffType?: string,
) => {
  return checkoutBasket?.ready_minutes && router.pathname === ROUTES.CHECKOUT
    ? checkoutBasket.ready_minutes
    : getLeadTimes(location, handoffType);
};

export function getFulfillmentTime(time: string | undefined, location: Location | undefined) {
  const { storeTimezone, storeDateTimeNow } = getStoreTimeInfo(location);

  if (!time || isAsapValue(time) || !location) {
    return TIME_MODES.ASAP.toUpperCase();
  }

  // Convert the input time to the store's timezone using the offset
  const fulfillmentTimeInStoreZone = utcToZonedTime(new Date(time), storeTimezone);

  if (isDaySame(storeDateTimeNow, fulfillmentTimeInStoreZone)) {
    return `Today, ${formatDate(fulfillmentTimeInStoreZone, DATE_FORMAT.HOURS_WITH_MINUTES)}`;
  }

  if (!isDaySame(storeDateTimeNow, fulfillmentTimeInStoreZone)) {
    return `${formatDate(fulfillmentTimeInStoreZone, DATE_FORMAT.WEEKDAY_HOURS_MINUTES)}`;
  }

  return formatDate(fulfillmentTimeInStoreZone, DATE_FORMAT.HOURS_WITH_MINUTES);
}

export const isAsapAvailable = (location?: Location, handoffType?: string) => {
  const { isOpen, hasRemainingHoursToday } = getStoreTimeInfo(location, handoffType);
  return supportsAsapOrders(location) && hasRemainingHoursToday && isOpen;
};

export const isAsapValue = (time: string | undefined | null) => !time || time === ASAP_VALUE;

export const getFirstAvailableTimeMs = (
  location: Location | undefined,
  fulfillmentType: CONVEYANCE_TYPES,
  day: string,
  storeTimezone: string,
  prepMinutes: number,
): number | null => {
  const firstOpen = getFirstOpenOnDay(location, fulfillmentType, day, storeTimezone);
  return firstOpen ? addMinutes(new Date(firstOpen), prepMinutes).getTime() : null;
};

export const getEarliestPrepTime = (
  earliestReadyTime: string,
  handoffTimeSelectOptions: { label: string; value: string }[],
) => {
  if (!earliestReadyTime || handoffTimeSelectOptions.length === 0) return null;
  const earliestTime = new Date(earliestReadyTime);

  return (
    handoffTimeSelectOptions.find(
      (slot) => new Date(slot.value).getTime() >= earliestTime.getTime(),
    )?.value ?? null
  );
};

export const getNextAvailableDayPrepTime = (
  currentEarliestReadyTime: string | null,
  originalEarliestReadyTime: string,
  availableDays: availableDaysType[],
  location: Location,
  fulfillmentType: string,
) => {
  if (currentEarliestReadyTime) return currentEarliestReadyTime;

  const earliestDay = formatDate(originalEarliestReadyTime, DATE_FORMAT.YEAR_MONTH_DAY_DASHED);
  const nextAvailableDay = availableDays.find((day) => !day.disabled && day.value >= earliestDay);

  if (nextAvailableDay?.value) {
    const leadTime = getLeadTimes(location, fulfillmentType);
    // round the earliestReadyTime to the nearest lead time minutes
    currentEarliestReadyTime = new Date(
      Math.ceil(new Date(originalEarliestReadyTime).getTime() / (leadTime * 60 * 1000)) *
        (leadTime * 60 * 1000),
    )?.toISOString();
  }
  return currentEarliestReadyTime;
};
